import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import './SubirHistoria.css';

// Cooldown global anti-duplicado: si se cierra el modal mientras una subida
// sigue sin confirmarse, bloquea reintentos inmediatos por unos segundos.
let subidaEnCursoHasta = 0;
let camaraDisponibleDesde = 0; // timestamp global: bloquea reabrir la camara muy rapido (hardware tarda en liberarse)

const SubirHistoria = ({ usuarioId, onClose }) => {
  const [fase, setFase] = useState('seleccion');
  const [preview, setPreview] = useState(null);
  const [archivoParaSubir, setArchivoParaSubir] = useState(null);
  const [tipoPreview, setTipoPreview] = useState(null);
  const [mensaje, setMensaje] = useState(null);
  const [mensajeTipo, setMensajeTipo] = useState('error');
  const [progreso, setProgreso] = useState(0);
  const exitoMostradoRef = useRef(false);
  const successTimeoutRef = useRef(null); // guarda el temporizador del cierre automatico para poder cancelarlo
  const subiendoRef = useRef(false); // bandera sincronica anti doble-tap
  const [camaraBloqueada, setCamaraBloqueada] = useState(() => Date.now() < camaraDisponibleDesde || Date.now() < subidaEnCursoHasta); // bloqueado si el cooldown de camara o una subida anterior todavia no terminan, incluso en un montaje nuevo
  const [bloqueoTexto, setBloqueoTexto] = useState(() => {
    if (Date.now() < subidaEnCursoHasta) return 'Tu historia anterior se sigue subiendo, espera unos segundos e intentalo de nuevo.';
    if (Date.now() < camaraDisponibleDesde) return 'Espera un momento antes de usar la camara de nuevo.';
    return null;
  });
  const [previewSilenciado, setPreviewSilenciado] = useState(true); // preview del video inicia silenciado (requerido para autoplay en navegadores)
  const previewVideoRef = useRef(null);

  const fileInputCamaraRef = useRef(null);
  const fileInputVideoRef = useRef(null);
  const fileInputGaleriaRef = useRef(null);

  const API_BASE = process.env.REACT_APP_HISTORIAS_CONTENIDO_URL;

// Bloquear scroll del body mientras se está subiendo (compatible iOS Safari y Android Chrome)
  useEffect(() => {
    // Bloquea el scroll del fondo durante TODO el tiempo que el modal
    // esta abierto (seleccion, preview, subiendo, exito), no solo mientras sube
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.overflow = 'hidden';
    document.body.dataset.scrollY = scrollY;

    return () => {
      const scrollYGuardado = parseInt(document.body.dataset.scrollY || '0');
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      delete document.body.dataset.scrollY;
      window.scrollTo(0, scrollYGuardado);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
    };
  }, []);


// Manejar archivo capturado con cámara nativa
  const manejarCamara = (e) => {
    const file = e.target.files[0];
    if (!file) {
      setMensaje('No se pudo capturar la foto o video. Intenta de nuevo.');
      setMensajeTipo('error');
      return;
    }
    camaraDisponibleDesde = Date.now() + 7000;
    setCamaraBloqueada(true);
    setBloqueoTexto('Espera un momento antes de usar la cámara de nuevo.');
    procesarArchivo(file, e);
  };

// Procesar archivo (cámara o galería)
  const procesarArchivo = (file, e) => {
    // Fallback por extensión: en móvil file.type puede venir vacío
    const ext = file.name.split('.').pop().toLowerCase();
    const extensionesVideo = ['mp4','mov','avi','mkv','wmv','flv','webm','3gp','m4v','ts','mpeg','mpg'];
    const extensionesFoto  = ['jpg','jpeg','png','gif','webp','heic','heif','bmp','tiff','tif','avif'];

    const esVideo = file.type.startsWith('video/') || extensionesVideo.includes(ext);
    const esFoto  = file.type.startsWith('image/') || extensionesFoto.includes(ext);

    if (!esVideo && !esFoto) {
      setMensaje('Solo se permiten fotos o videos.');
      setMensajeTipo('error');
      return;
    }

    if (esVideo) {
      setFase('procesando');
      setMensaje(null);
      const url = URL.createObjectURL(file);
      const videoEl = document.createElement('video');
      videoEl.preload = 'metadata';

      let yaResuelto = false;

      const procederConVideo = (duracion) => {
        if (yaResuelto) return;
        yaResuelto = true;
        clearTimeout(timeoutFallback);
        URL.revokeObjectURL(url);

        if (duracion !== null && duracion > 45) {
          setMensaje('El video supera los 45 segundos. Máximo 45 segundos permitidos.');
          setMensajeTipo('error');
          setFase('seleccion');
          if (e?.target) e.target.value = '';
          return;
        }
        const previewUrl = URL.createObjectURL(file);
        setArchivoParaSubir(file);
        setPreview(previewUrl);
        setTipoPreview('video');
        setFase('preview');
        setMensaje(null);
      };

      // Fallback: si onloadedmetadata no dispara en 4s (iOS/Android con .mov, etc.),
      // omitir validación de duración y avanzar igual
      const timeoutFallback = setTimeout(() => {
        procederConVideo(null);
      }, 4000);

      videoEl.onloadedmetadata = () => {
        procederConVideo(videoEl.duration);
      };

      videoEl.onerror = () => {
        if (yaResuelto) return;
        yaResuelto = true;
        clearTimeout(timeoutFallback);
        URL.revokeObjectURL(url);
        setMensaje('No se pudo leer el video.');
        setMensajeTipo('error');
        setFase('seleccion');
      };

      videoEl.src = url;
      videoEl.load(); // necesario en Safari móvil para disparar onloadedmetadata
    } else {
      const previewUrl = URL.createObjectURL(file);
      setArchivoParaSubir(file);
      setPreview(previewUrl);
      setTipoPreview('foto');
      setFase('preview');
      setMensaje(null);
    }
  };

  // Manejar archivo de galería
  const manejarArchivo = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    procesarArchivo(file, e);
  };

// Subir historia
  const subirHistoria = async () => {
    if (!archivoParaSubir) return;
    if (subiendoRef.current) return;
    const restanteCooldown = subidaEnCursoHasta - Date.now();
    if (restanteCooldown > 0) {
      setMensaje('Tu historia anterior se sigue procesando, espera ' + Math.ceil(restanteCooldown / 1000) + ' segundos antes de subir otra.');
      setMensajeTipo('error');
      return;
    }
    subiendoRef.current = true;
    subidaEnCursoHasta = Date.now() + 15000;
    setCamaraBloqueada(true);
    setBloqueoTexto('Tu historia anterior se sigue subiendo, espera unos segundos e intentalo de nuevo.');
    setFase('subiendo');
    setProgreso(0);
    exitoMostradoRef.current = false;

    const formData = new FormData();
    formData.append('archivo', archivoParaSubir);
    formData.append('usuario_id', usuarioId);

    try {
      const response = await axios.post(`${API_BASE}/subir_historia.php`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const porcentaje = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setProgreso(porcentaje);
        }
      });

      // Mostrar éxito o error segun la respuesta REAL del servidor,
      // no segun el progreso de subida (que solo mide bytes enviados)
      if (response.data.success) {
        exitoMostradoRef.current = true;
        subidaEnCursoHasta = 0;
        setFase('exito');
        successTimeoutRef.current = setTimeout(() => { onClose(true); }, 6000);
      } else {
        setMensaje(response.data.message || 'Error al subir la historia.');
        setMensajeTipo('error');
        setFase('seleccion');
        subiendoRef.current = false;
        subidaEnCursoHasta = 0;
      }
    } catch (err) {
      setMensaje('Error de conexión al subir la historia.');
      setMensajeTipo('error');
      setFase('seleccion');
      subiendoRef.current = false;
      subidaEnCursoHasta = 0;
    }
  };

const handleClose = () => {
    if (fase === 'subiendo') {
      subidaEnCursoHasta = Date.now() + 10000;
    }
    onClose();
  };

  const volverASeleccion = () => {
    if (previewVideoRef.current) {
      previewVideoRef.current.pause();
      previewVideoRef.current.removeAttribute('src');
      previewVideoRef.current.load();
    }
    if (preview) {
      URL.revokeObjectURL(preview);
    }
    setPreview(null);
    setArchivoParaSubir(null);
    setMensaje(null);
    setFase('seleccion');
    if (fileInputCamaraRef.current) fileInputCamaraRef.current.value = '';
    if (fileInputGaleriaRef.current) fileInputGaleriaRef.current.value = '';
  };

  return (
    <div className="sh-overlay">
      <div className="sh-container">

        {/* Header */}
        <div className="sh-header">
          <span className="sh-title">Subir Historia</span>
          <button className="sh-close-btn" onClick={handleClose}>X</button>
        </div>

        {/* Info l�mites */}
        <div className="sh-limits-info">
          Maximo: 15 fotos - 10 videos - 45 seg por video - Duracion: 24 horas
        </div>

        {/* Mensaje */}
        {mensaje && (
          <div className={`sh-mensaje sh-mensaje-${mensajeTipo}`}>
            {mensaje}
          </div>
        )}

{/* FASE: Selección */}
        {fase === 'seleccion' && (
          <div className="sh-seleccion">
            {camaraBloqueada && (
              <div className="sh-mensaje sh-mensaje-error">{bloqueoTexto} Si sigue bloqueado, cierra esta ventana y vuelve a abrir "Subir Historia".</div>
            )}
            <div className={"sh-opcion" + (camaraBloqueada ? " sh-opcion-disabled" : "")} onClick={() => {
              if (camaraBloqueada) return;
              fileInputCamaraRef.current?.click();
            }}>
              <div className="sh-opcion-icono">
📷</div>
              <div className="sh-opcion-label">Tomar foto</div>
            </div>
            <div className={"sh-opcion" + (camaraBloqueada ? " sh-opcion-disabled" : "")} onClick={() => {
              if (camaraBloqueada) return;
              fileInputVideoRef.current?.click();
            }}>
              <div className="sh-opcion-icono">
🎬</div>
              <div className="sh-opcion-label">Grabar video</div>
            </div>
            <div className={"sh-opcion" + (camaraBloqueada ? " sh-opcion-disabled" : "")} onClick={() => {
              if (camaraBloqueada) return;
              fileInputGaleriaRef.current?.click();
            }}>
              <div className="sh-opcion-icono">
🖼️</div>
              <div className="sh-opcion-label">Galería</div>
            </div>

            {/* Input foto con cámara — accept image/* fuerza modo foto en todos los Android */}
            <input
              ref={fileInputCamaraRef}
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: 'none' }}
              onChange={manejarCamara}
            />

            {/* Input video con cámara — accept video/* fuerza modo video en todos los Android */}
            <input
              ref={fileInputVideoRef}
              type="file"
              accept="video/*"
              capture="environment"
              style={{ display: 'none' }}
              onChange={manejarCamara}
            />

            {/* Input galería — sin capture para mostrar archivos existentes */}
            <input
              ref={fileInputGaleriaRef}
              type="file"
              accept="image/*,video/*"
              style={{ display: 'none' }}
              onChange={manejarArchivo}
            />
          </div>
        )}

        {/* FASE: Procesando */}
        {fase === 'procesando' && (
          <div className="sh-procesando">
            <div className="sh-spinner"></div>
            <p>Procesando video, espera un momento...</p>
          </div>
        )}

        {/* FASE: Preview */}
        {fase === 'preview' && (
          <div className="sh-preview-fase">
            {tipoPreview === 'foto' ? (
              <img src={preview} alt="Vista previa" className="sh-preview-media" />
            ) : (
              <div className="sh-preview-video-wrap">
                <video
                  ref={previewVideoRef}
                  src={preview}
                  playsInline
                  muted={previewSilenciado}
                  loop
                  autoPlay
                  disableRemotePlayback
                  disablePictureInPicture
                  className="sh-preview-media"
                  onClick={() => {
                    if (previewVideoRef.current) {
                      if (previewVideoRef.current.paused) previewVideoRef.current.play();
                      else previewVideoRef.current.pause();
                    }
                  }}
                />
                  <button type="button" className="sh-preview-play-btn" onClick={() => {
                    if (previewVideoRef.current) {
                      if (previewVideoRef.current.paused) previewVideoRef.current.play();
                      else previewVideoRef.current.pause();
                    }
                  }}>
                    ▶ / ❚❚
                  </button>
                <button type="button" className="sh-preview-sonido-btn" onClick={() => setPreviewSilenciado(!previewSilenciado)}>
                  {previewSilenciado ? '🔇' : '🔊'}
                </button>
              </div>
            )}
            <div className="sh-preview-btns">
              <button className="sh-btn sh-btn-secondary" onClick={volverASeleccion}>
                Reintentar
              </button>
              <button className="sh-btn sh-btn-primary" onClick={subirHistoria}>
                Aceptar
              </button>
            </div>
          </div>
        )}

{/* FASE: Subiendo */}
        {fase === 'subiendo' && (
          <div className="sh-subiendo">
            {progreso < 100 ? (
              <p style={{ color: '#ff4d4d', fontWeight: 'bold', fontSize: '15px' }}>
                NO MINIMICES NI SALGAS DE ESTA PANTALLA MIENTRAS SE SUBE EL ARCHIVO. Si lo haces, el archivo NO se subira.
              </p>
            ) : (
              <p>
                Procesando y verificando el archivo. Puedes cerrar esta ventana, la subida continua en segundo plano y tu historia aparecera en unos minutos.
              </p>
            )}
            <div className="sh-barra-container">
              <div 
                className="sh-barra-fill"
                style={{ width: `${progreso}%` }}
              ></div>
            </div>
            <p className="sh-progreso-texto">{progreso}%</p>
            {progreso === 100 && (
              <button className="sh-btn sh-btn-secondary" onClick={handleClose}>
                Cerrar y seguir navegando
              </button>
            )}
          </div>
        )}

        {/* FASE: Éxito */}
        {fase === 'exito' && (
          <div className="sh-exito">
            <div className="sh-exito-icono">✓</div>
            <p>¡Historia publicada!</p>
            {tipoPreview === 'video' && (
              <p className="sh-exito-aviso">
                El video estará visible en ~1 minuto mientras se procesa en el servidor.
              </p>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default SubirHistoria;